/**
 * API client.
 *
 * Two things it has to get right that a bare `fetch` wrapper would not:
 *
 *  1. A 401 mid-session is normal — the access token is short-lived. The
 *     client refreshes once and replays the request, so the user never sees it.
 *  2. Several requests can hit a stale token at the same moment. They share
 *     one in-flight refresh rather than each rotating the token, which would
 *     trip the server's reuse detection and log the user out.
 */

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

const ACCESS_KEY = 'accel.access';
const REFRESH_KEY = 'accel.refresh';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/** Notifies the app when the session ends so it can route back to login. */
type SessionEndedHandler = () => void;
let onSessionEnded: SessionEndedHandler = () => {};
export function setSessionEndedHandler(fn: SessionEndedHandler): void {
  onSessionEnded = fn;
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  // Collapse concurrent refreshes into one; rotating twice would invalidate
  // the whole token family on the server.
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = tokens.refresh;
    if (!refreshToken) return false;

    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;

      const session = (await res.json()) as { accessToken: string; refreshToken: string };
      tokens.set(session.accessToken, session.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /**
   * Send `body` as multipart rather than JSON. The browser sets the
   * `Content-Type` itself, boundary and all, so this suppresses ours.
   */
  multipart?: boolean;
  /** Sent as Idempotency-Key; required for money-moving actions. */
  idempotencyKey?: string;
  /** Skip the bearer token and the refresh dance (login, refresh). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = options.multipart
      ? {}
      : { 'Content-Type': 'application/json' };

    if (!options.anonymous) {
      const access = tokens.access;
      if (access) headers['Authorization'] = `Bearer ${access}`;
    }
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

    const body = options.multipart
      ? (options.body as BodyInit)
      : options.body !== undefined
        ? JSON.stringify(options.body)
        : undefined;

    return fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      signal: options.signal,
      ...(body !== undefined ? { body } : {}),
    });
  };

  let res = await send();

  if (res.status === 401 && !options.anonymous) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      res = await send();
    } else {
      tokens.clear();
      onSessionEnded();
      throw new ApiError(401, 'Your session has expired. Please sign in again.');
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }

  if (!res.ok) {
    const body = payload as { message?: string | string[]; code?: string } | null;
    const raw = body?.message;
    // class-validator returns an array of messages; show them as one line.
    const message = Array.isArray(raw) ? raw.join('; ') : (raw ?? `Request failed (${res.status})`);
    throw new ApiError(res.status, message, body?.code, payload);
  }

  return payload as T;
}

/**
 * Fetch a file the session is entitled to.
 *
 * A plain `<a href>` cannot carry the bearer token, and the download endpoint
 * deliberately serves `attachment` with `nosniff` rather than something a
 * link could open inline. So the bytes come back through the same client —
 * refresh and replay included — and are handed to the browser as a blob.
 */
async function download(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const res = await requestRaw(path);
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return {
    blob: await res.blob(),
    filename: match?.[1] ? decodeURIComponent(match[1]) : null,
  };
}

/** Like `request`, but hands back the response so a caller can read bytes. */
async function requestRaw(path: string): Promise<Response> {
  const send = () =>
    fetch(`${BASE}${path}`, {
      headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
    });

  let res = await send();
  if (res.status === 401) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      res = await send();
    } else {
      tokens.clear();
      onSessionEnded();
      throw new ApiError(401, 'Your session has expired. Please sign in again.');
    }
  }
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(res.status, detail?.message ?? `Request failed (${res.status})`);
  }
  return res;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
  /** Multipart POST — an upload, which cannot go through the JSON path. */
  upload: <T>(path: string, form: FormData, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body: form, multipart: true }),
  download,
};
